// src/cockpit4/components/GEODSOEntry.tsx
// ============================================
// GEO+DSO 入口面板 - 集成到营销中心
//
// 功能：
//   1. 从 GEORuntime 读取今日内容策略（基于 Obsidian 真实数据）
//   2. 从 DSORuntime 读取分发计划（基于品牌圣经真实数据）
//   3. 显示矩阵账号状态（基于发布记录真实数据）
//   4. 一键启动 GrowthFactory 编排（GEO→DSO→Content→Publish）
//   5. 显示 SEO 机会和关键词研究
//
// 数据来源：RuntimeManager → GEORuntime / DSORuntime / GrowthFactoryRuntime
// 不依赖外部 Python 后端，全部通过 XEAOS Runtime 体系
// ============================================

import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Globe,
  TrendingUp,
  RefreshCw,
  Loader2,
  Search,
  FileText,
  Brain,
  CheckCircle2,
  XCircle,
  Play,
  Target,
  Share2,
  AlertCircle,
  Send,
  ExternalLink,
  ArrowRight,
  Database,
  Layers,
  Factory,
  Activity,
  ChevronDown,
  Sparkles,
  BookOpen,
  ShieldCheck,
  Copy,
  BadgeCheck,
  Radar,
  Phone,
  Grid3x3,
  BookMarked,
  Braces,
  Network,
  Swords,
  FlaskConical,
  Route,
  ListChecks,
  ClipboardList,
  Compass,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import type { RuntimeHealth } from "../../types/runtime";
import { useCockpit4Store } from "../store";
import AiVisibilityMonitor from "./AiVisibilityMonitor";
import LeadCapturePanel from "./LeadCapturePanel";
import GeoStrategyCanvas from "./GeoStrategyCanvas";
import BrandLibraryPanel from "./BrandLibraryPanel";
import SchemaDeployerPanel from "./SchemaDeployerPanel";
import ProductCapabilityPanel from "./ProductCapabilityPanel";
import SourceDeploymentPanel from "./SourceDeploymentPanel";
import GeoInputEnhanced from "./GeoInputEnhanced";
import DsoDecisionEngine from "./DsoDecisionEngine";
import GfOutputClosure from "./GfOutputClosure";
import GeoDsoCommandCenter from "./GeoDsoCommandCenter";
import KnowledgeGraphPanel from "./KnowledgeGraphPanel";
import CompetitorVisibilityPanel from "./CompetitorVisibilityPanel";
import ContentABExperimentPanel from "./ContentABExperimentPanel";
import LeadRoutingPanel from "./LeadRoutingPanel";
import GeoDsoGuidePanel from "./GeoDsoGuidePanel";

// ============================================
// 类型
// ============================================

interface CoreKeyword {
  keyword: string;
  searchVolume: number;
  competition: number;
  difficulty: 'low' | 'medium' | 'high';
  platforms: string[];
  cpc: number;
  intent: 'informational' | 'commercial' | 'transactional' | 'navigational';
  serpFeatures: string[];
  trend: 'rising' | 'stable' | 'declining';
}

interface ContentDirection {
  title: string;
  keyword: string;
  platform: string;
  reason: string;
  targetKeywords: string[];
  estimatedCTR: number;
  estimatedTraffic: number;
  conversionPotential: number;
  roiScore: number;
  contentType: 'tutorial' | 'analysis' | 'case_study' | 'opinion' | 'news';
  recommendedWordCount: number;
  /** GEO 专业增强（v5.2）：目标投放模型 */
  primaryModel?: string;
  /** GEO 专业增强（v5.2）：独立成立的知识块（可被模型摘取复述） */
  knowledgeBlocks?: { question: string; answer: string; source?: string }[];
  /** GEO 专业增强（v5.2）：E-E-A-T（经验-专业-权威-信任）与事实核验 */
  eeaT?: {
    authorIdentity: string;
    citationSources: string[];
    factCheckStatus: 'verified' | 'pending' | 'not_applicable';
  };
}

interface SEOOpportunity {
  keyword: string;
  platform: string;
  opportunity: number;
  serpFeature: string;
  contentGap: string;
  competitorStrength: number;
}

interface ContentGap {
  topic: string;
  competitor: string;
  estimatedTraffic: number;
  difficulty: 'low' | 'medium' | 'high';
  action: string;
}

interface DistributionTask {
  contentTitle: string;
  sourcePlatform: string;
  targetPlatform: string;
  repurposeSuggestion: string;
  bestTime: string;
  estimatedReach: number;
  priority: string;
  /** GEO 专业增强（v5.2）：是否官方首发 */
  isFirstPublish?: boolean;
  /** GEO 专业增强（v5.2）：目标投放模型 */
  targetModel?: string;
  /** GEO 专业增强（v5.2）：差异化改写度 */
  rewriteDegree?: 'none' | 'light' | 'medium' | 'deep';
  /** GEO 专业增强（v5.2）：目标模型内容生态 */
  modelEcosystem?: string;
  /** GEO 专业增强（v5.2）：重复内容风险提示 */
  duplicateRisk?: string;
}

interface MatrixPlatform {
  platform: string;
  accountName: string;
  followers: number;
  todayPublished: number;
  weeklyEngagement: number;
  health: number;
  status: string;
}

// ============================================
// 平台显示配置
// ============================================

const PLATFORM_DISPLAY: Record<string, { name: string; color: string; icon: string }> = {
  wechat: { name: '微信公众号', color: '#07C160', icon: '💬' },
  xiaohongshu: { name: '小红书', color: '#FF2442', icon: '📕' },
  douyin: { name: '抖音', color: '#000000', icon: '🎵' },
  zhihu: { name: '知乎', color: '#0084FF', icon: '💡' },
  bilibili: { name: 'B站', color: '#FB7299', icon: '📺' },
  google: { name: 'Google', color: '#4285F4', icon: '🔍' },
  baidu: { name: '百度', color: '#2932E1', icon: '🐼' },
  bing: { name: 'Bing', color: '#008373', icon: '🟢' },
};

const DIFFICULTY_COLOR: Record<string, string> = {
  low: '#10B981',
  medium: '#F59E0B',
  high: '#EF4444',
};

// ============================================
// 健康度配色与文案（healthy绿 / degraded黄 / unhealthy红 / unknown灰）
// ============================================
const HEALTH_COLOR: Record<RuntimeHealth, string> = {
  healthy: '#10B981',
  degraded: '#F59E0B',
  unhealthy: '#EF4444',
  unknown: '#6B7280',
};

const HEALTH_LABEL: Record<RuntimeHealth, string> = {
  healthy: '健康',
  degraded: '降级',
  unhealthy: '异常',
  unknown: '未知',
};

// ============================================
// 4 步收益 SOP 面板定义
// ============================================
const SOP_STEPS = [
  {
    id: 'source',
    title: '信源铺设指引',
    desc: '铺设品牌事实库、Schema、产品能力，确保持续可被 AI 搜索引用',
    icon: '📡',
    color: '#3B82F6',
    detailTabs: ['source', 'brand', 'schema', 'capability'],
  },
  {
    id: 'plan',
    title: '计划登记',
    desc: '登记 GEO 策略与 DSO 分发计划，绑定目标关键词与平台矩阵',
    icon: '📋',
    color: '#F59E0B',
    detailTabs: ['geoinput', 'strategy', 'dso'],
  },
  {
    id: 'review',
    title: '数据复盘',
    desc: '周报/月报复盘：AI 可见性、收录率、线索转化效率',
    icon: '📊',
    color: '#10B981',
    detailTabs: ['visibility', 'gf', 'command'],
  },
  {
    id: 'convert',
    title: '收益转化',
    desc: '对外报价、线索评分、CRM 跟进，闭环 ROI 反哺策略迭代',
    icon: '💰',
    color: '#EC4899',
    detailTabs: ['leads', 'routing', 'ab'],
  },
];

// ============================================
// 主组件
// ============================================

export default function GEODSOEntry() {
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [activeTab, setActiveTab] = useState<'guide' | 'strategy' | 'seo' | 'distribution' | 'matrix' | 'gaps' | 'visibility' | 'leads' | 'canvas' | 'brand' | 'schema' | 'capability' | 'source' | 'geoinput' | 'dso' | 'gf' | 'command' | 'kg' | 'compete' | 'ab' | 'routing'>('guide');
  // 当前选中的 4 步 SOP 步骤（用于面板高亮；切换 Tab 时自动联动）
  const [activeSop, setActiveSop] = useState<string>('source');
  const [error, setError] = useState<string | null>(null);

  // 数据状态
  const [coreKeywords, setCoreKeywords] = useState<CoreKeyword[]>([]);
  const [contentDirections, setContentDirections] = useState<ContentDirection[]>([]);
  const [brandKeywords, setBrandKeywords] = useState<string[]>([]);
  const [seoOpportunities, setSeoOpportunities] = useState<SEOOpportunity[]>([]);
  const [contentGaps, setContentGaps] = useState<ContentGap[]>([]);
  const [distributionTasks, setDistributionTasks] = useState<DistributionTask[]>([]);
  const [matrixPlatforms, setMatrixPlatforms] = useState<MatrixPlatform[]>([]);
  const [matrixSynergy, setMatrixSynergy] = useState<any[]>([]);
  const [brandChecklist, setBrandChecklist] = useState<string[]>([]);
  const [dataSourceMeta, setDataSourceMeta] = useState<any>(null);

  // ============ v5.2.3 GEO ↔ 资产库关联 ============
  // 资产库产物索引：{ 标题 → { path, platform, createdAt } }，用于内容方向卡片显示"已有产物"关联
  const [assetIndex, setAssetIndex] = useState<Map<string, { path: string; platform: string; createdAt: string }>>(new Map());
  const [assetTotal, setAssetTotal] = useState(0);
  const [leadTotal, setLeadTotal] = useState<number | null>(null);

  // 加载资产库产物 + CRM 线索数（GEO 获客闭环的产出侧关联）
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // 1. 资产库产物（XEAOS/ContentPackages/）
        const rootResp = await fetch(`/vault-api/list?dir=${encodeURIComponent('XEAOS/ContentPackages')}&ext=.md`);
        if (rootResp.ok) {
          const rootJson = await rootResp.json();
          const dateDirs: any[] = (rootJson.files || []).filter((f: any) => f.isDirectory);
          const idx = new Map<string, { path: string; platform: string; createdAt: string }>();
          let total = 0;
          for (const dir of dateDirs) {
            const subResp = await fetch(`/vault-api/list?dir=${encodeURIComponent(dir.path)}&ext=.md`);
            if (!subResp.ok) continue;
            const subJson = await subResp.json();
            for (const file of (subJson.files || [])) {
              const name = String(file.name || '').replace(/\.md$/, '');
              // 文件名格式：script-{platform}-{ts}-{topic}（topic 含 emoji 与空格）
              const parts = name.split('-');
              const platform = parts[1] || '';
              const topic = parts.slice(3).join('-').trim();
              if (!topic) continue;
              if (!idx.has(topic)) {
                idx.set(topic, { path: file.path, platform, createdAt: file.mtime || '' });
                total++;
              }
            }
          }
          if (!cancelled) { setAssetIndex(idx); setAssetTotal(total); }
        }
        // 2. CRM 线索数（GrowthFactory 获客侧）
        if (typeof RuntimeManager !== 'undefined' && RuntimeManager.isInitialized()) {
          try {
            const leadResp = await RuntimeManager.query(RuntimeName.GrowthFactory as any, { type: 'growth_overview' });
            const wrapped = (leadResp as any)?.data as any;
            const inner = (wrapped?.data && typeof wrapped.data === 'object') ? wrapped.data : wrapped;
            const funnel = inner?.leadFunnel || inner?.data?.leadFunnel;
            if (!cancelled) setLeadTotal(funnel?.leads ?? null);
          } catch { /* 忽略 */ }
        }
      } catch { /* 忽略 */ }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // 三层架构健康度（GEO 输入层 / DSO 决策层 / GrowthFactory 输出层）
  const [geoHealth, setGeoHealth] = useState<RuntimeHealth>('unknown');
  const [dsoHealth, setDsoHealth] = useState<RuntimeHealth>('unknown');
  const [gfHealth, setGfHealth] = useState<RuntimeHealth>('unknown');
  // 三层架构执行中状态（GEO/DSO 各自独立，GrowthFactory 复用顶部 running 状态）
  const [geoRunning, setGeoRunning] = useState(false);
  const [dsoRunning, setDsoRunning] = useState(false);
  // GrowthFactory 执行结果摘要（输出层）
  const [gfLastResult, setGfLastResult] = useState<{ success: boolean; time: string; detail?: string } | null>(null);
  // GEO / DSO 单层执行结果反馈（成功/失败提示）
  const [geoLastResult, setGeoLastResult] = useState<{ success: boolean; time: string; error?: string } | null>(null);
  const [dsoLastResult, setDsoLastResult] = useState<{ success: boolean; time: string; error?: string } | null>(null);
  // GrowthFactory 完整 pipeline 结果（9 stage 详情）
  const [lastPipeline, setLastPipeline] = useState<{
    success: boolean;
    time: string;
    completedStages: number;
    totalStages: number;
    stages: Array<{ name: string; status: string; detail: string; error?: string; items: number }>;
  } | null>(null);
  // pipeline 详情展开开关
  const [showPipelineDetail, setShowPipelineDetail] = useState(false);
  // 顶部 toast 提示（自动消失）
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // ============================================
  // 刷新三层架构健康度（GEO / DSO / GrowthFactory）
  // 读取 RuntimeManager 中各 Runtime 的 getInfo().health
  // ============================================
  const refreshLayerHealth = useCallback(() => {
    const checkHealth = (name: RuntimeName): RuntimeHealth => {
      if (!RuntimeManager.hasRuntime(name)) return 'unknown';
      try {
        return RuntimeManager.getRuntime(name).getInfo().health;
      } catch {
        return 'unknown';
      }
    };
    setGeoHealth(checkHealth(RuntimeName.GEO));
    setDsoHealth(checkHealth(RuntimeName.DSO));
    setGfHealth(checkHealth(RuntimeName.GrowthFactory));
  }, []);

  // ============================================
  // 加载数据
  // ============================================
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 等待 RuntimeManager 就绪（最长等待 30 秒，因为浏览器环境有 40+ Runtime 需要初始化）
      let waitedMs = 0;
      for (let i = 0; i < 150; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 200));
        waitedMs += 200;
      }
      console.log(`[GEODSOEntry] RuntimeManager 等待 ${waitedMs}ms, initialized=${RuntimeManager.isInitialized()}`);

      if (!RuntimeManager.isInitialized()) {
        setError('RuntimeManager 初始化超时（30秒），请刷新页面重试');
        return;
      }

      const hasGEO = RuntimeManager.hasRuntime(RuntimeName.GEO);
      const hasDSO = RuntimeManager.hasRuntime(RuntimeName.DSO);
      console.log('[GEODSOEntry] GEO registered:', hasGEO, 'DSO registered:', hasDSO);
      console.log('[GEODSOEntry] All runtimes:', RuntimeManager.getAllRuntimes());

      if (!hasGEO || !hasDSO) {
        const allRuntimes = RuntimeManager.getAllRuntimes();
        const count = RuntimeManager.runtimeCount;
        setError(
          `GEO/DSO Runtime 未注册（GEO=${hasGEO}, DSO=${hasDSO}）。\n` +
          `已注册 ${count} 个 Runtime: ${JSON.stringify(allRuntimes)}。\n` +
          `请检查控制台 [RuntimeManager] 和 [register] 日志。`
        );
        return;
      }

      // 并行查询 GEO 和 DSO
      const [geoResp, dsoResp] = await Promise.all([
        RuntimeManager.query(RuntimeName.GEO, { type: 'today_strategy' }),
        RuntimeManager.query(RuntimeName.DSO, { type: 'distribution_plan' }),
      ]);

      // 双层解包 GEO
      const geoInner = geoResp?.data as any;
      const geoData = geoInner?.success ? geoInner.data : geoInner;
      if (geoData) {
        setCoreKeywords(geoData.coreKeywords || []);
        setContentDirections(geoData.contentDirections || []);
        setBrandKeywords(geoData.brandKeywords || []);
        setSeoOpportunities(geoData.seoOpportunities || []);
        setContentGaps(geoData.contentGaps || []);
        if (geoData._meta) setDataSourceMeta(geoData._meta);
      }

      // 双层解包 DSO
      const dsoInner = dsoResp?.data as any;
      const dsoData = dsoInner?.success ? dsoInner.data : dsoInner;
      if (dsoData) {
        setDistributionTasks(dsoData.tasks || []);
        setMatrixSynergy(dsoData.matrixSynergy || []);
        setBrandChecklist(dsoData.brandChecklist || []);
        if (!dataSourceMeta && dsoData._meta) setDataSourceMeta(dsoData._meta);
      }

      // 查询矩阵状态
      try {
        const matrixResp = await RuntimeManager.query(RuntimeName.DSO, { type: 'matrix_status' });
        const matrixInner = matrixResp?.data as any;
        const matrixData = matrixInner?.success ? matrixInner.data : matrixInner;
        if (matrixData?.platforms) {
          setMatrixPlatforms(matrixData.platforms);
        }
      } catch {
        // 降级
      }
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
      // 刷新三层架构健康度（无论加载是否成功，都反映当前 Runtime 状态）
      refreshLayerHealth();
    }
  }, [refreshLayerHealth]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 允许子面板（如 AI 可见性监测）一键跳转到指定 tab（信源铺设登记等）
  useEffect(() => {
    const onGotoTab = (e: Event) => {
      const tab = (e as CustomEvent<string>).detail;
      if (tab) setActiveTab(tab as never);
    };
    window.addEventListener("xeaos:goto-tab", onGotoTab);
    return () => window.removeEventListener("xeaos:goto-tab", onGotoTab);
  }, []);

  // toast 自动消失（3 秒后清空）
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  // ============================================
  // 一键启动 GrowthFactory 编排（输出层执行）
  // 同时驱动顶部"一键启动"按钮和三层架构中 GrowthFactory 卡片的"执行"按钮
  // ============================================
  const handleRunGrowthFactory = async () => {
    setRunning(true);
    setError(null);
    try {
      if (!RuntimeManager.hasRuntime(RuntimeName.GrowthFactory)) {
        setError('GrowthFactory Runtime 未注册');
        setGfLastResult({ success: false, time: new Date().toLocaleTimeString('zh-CN'), detail: 'Runtime 未注册' });
        setToast({ message: '❌ GrowthFactory Runtime 未注册', type: 'error' });
        return;
      }
      const resp = await RuntimeManager.execute(
        RuntimeName.GrowthFactory,
        'run_daily_growth',
        {}
      );
      if (resp?.success) {
        // 编排成功：记录结果、提取 pipeline、显示 toast
        const pipelineData = (resp as any)?.data?.pipeline;
        const time = new Date().toLocaleTimeString('zh-CN');
        setGfLastResult({ success: true, time });
        setToast({ message: '✅ 增长流程执行完成', type: 'success' });
        if (pipelineData && Array.isArray(pipelineData.stages)) {
          // 提取 9 个 stage 的成功/失败状态
          setLastPipeline({
            success: true,
            time,
            completedStages: pipelineData.completedStages ?? 0,
            totalStages: pipelineData.totalStages ?? pipelineData.stages.length,
            stages: pipelineData.stages.map((s: any) => ({
              name: s.name,
              status: s.status,
              detail: s.detail || '',
              error: s.error,
              items: s.items ?? 0,
            })),
          });
        }
        await loadData();
      } else {
        const errMsg = resp?.error || '编排启动失败';
        setError(errMsg);
        setGfLastResult({ success: false, time: new Date().toLocaleTimeString('zh-CN'), detail: errMsg });
        setToast({ message: `❌ 增长流程执行失败：${errMsg}`, type: 'error' });
        // 失败时也记录 pipeline 摘要（空 stages）
        setLastPipeline({
          success: false,
          time: new Date().toLocaleTimeString('zh-CN'),
          completedStages: 0,
          totalStages: 9,
          stages: [],
        });
      }
    } catch (err: any) {
      const errMsg = err?.message || String(err);
      setError(errMsg);
      setGfLastResult({ success: false, time: new Date().toLocaleTimeString('zh-CN'), detail: errMsg });
      setToast({ message: `❌ 增长流程执行失败：${errMsg}`, type: 'error' });
    } finally {
      setRunning(false);
      // 刷新三层架构健康度
      refreshLayerHealth();
    }
  };

  // ============================================
  // 执行 GEO 层 - 刷新策略（输入层：热点采集/关键词/SEO机会/内容方向）
  // ============================================
  const handleExecuteGEO = async () => {
    if (geoRunning) return;
    setGeoRunning(true);
    setError(null);
    try {
      if (!RuntimeManager.hasRuntime(RuntimeName.GEO)) {
        setError('GEO Runtime 未注册');
        setGeoLastResult({ success: false, time: new Date().toLocaleTimeString('zh-CN'), error: 'Runtime 未注册' });
        return;
      }
      const resp = await RuntimeManager.execute(RuntimeName.GEO, 'generate_strategy', {});
      if (resp?.success) {
        // 执行成功：记录结果并刷新对应层数据
        setGeoLastResult({ success: true, time: new Date().toLocaleTimeString('zh-CN') });
        await loadData();
      } else {
        const errMsg = resp?.error || 'GEO 策略刷新失败';
        setError(errMsg);
        setGeoLastResult({ success: false, time: new Date().toLocaleTimeString('zh-CN'), error: errMsg });
      }
    } catch (err: any) {
      const errMsg = err?.message || String(err);
      setError(errMsg);
      setGeoLastResult({ success: false, time: new Date().toLocaleTimeString('zh-CN'), error: errMsg });
    } finally {
      setGeoRunning(false);
      refreshLayerHealth();
    }
  };

  // ============================================
  // 执行 DSO 层 - 刷新分发（决策层：分发任务/矩阵协同/品牌一致性）
  // ============================================
  const handleExecuteDSO = async () => {
    if (dsoRunning) return;
    setDsoRunning(true);
    setError(null);
    try {
      if (!RuntimeManager.hasRuntime(RuntimeName.DSO)) {
        setError('DSO Runtime 未注册');
        setDsoLastResult({ success: false, time: new Date().toLocaleTimeString('zh-CN'), error: 'Runtime 未注册' });
        return;
      }
      const resp = await RuntimeManager.execute(RuntimeName.DSO, 'generate_distribution_plan', {});
      if (resp?.success) {
        // 执行成功：记录结果并刷新对应层数据
        setDsoLastResult({ success: true, time: new Date().toLocaleTimeString('zh-CN') });
        await loadData();
      } else {
        const errMsg = resp?.error || 'DSO 分发刷新失败';
        setError(errMsg);
        setDsoLastResult({ success: false, time: new Date().toLocaleTimeString('zh-CN'), error: errMsg });
      }
    } catch (err: any) {
      const errMsg = err?.message || String(err);
      setError(errMsg);
      setDsoLastResult({ success: false, time: new Date().toLocaleTimeString('zh-CN'), error: errMsg });
    } finally {
      setDsoRunning(false);
      refreshLayerHealth();
    }
  };

  // ============================================
  // 发布到微信公众号（含发布状态验证）
  // ============================================
  const [publishingWechat, setPublishingWechat] = useState(false);
  const [publishMessage, setPublishMessage] = useState<string | null>(null);
  const [publishStatus, setPublishStatus] = useState<'idle' | 'submitting' | 'verifying' | 'success' | 'failed'>('idle');
  const setWorkspace = useCockpit4Store((s) => s.setWorkspace);

  /** 内容方向 → 内容工厂（跳转并带入话题，打通 GEO→内容 最小闭环衔接） */
  const handleGoGenerate = (dir: ContentDirection) => {
    try {
      localStorage.setItem("xeaos_goto_topic", dir.title || "");
      localStorage.setItem("xeaos_goto_keyword", dir.keyword || "");
    } catch { /* 忽略 */ }
    setWorkspace("media_studio");
  };

  // v6.3.1 DSO 分发任务 → 内容工厂生成对应平台脚本（打通 DSO→Content 闭环）
  const handleGoGenerateTask = (task: DistributionTask) => {
    try {
      localStorage.setItem("xeaos_goto_topic", task.contentTitle || "");
      localStorage.setItem("xeaos_goto_platform", task.targetPlatform || "wechat");
      localStorage.setItem("xeaos_goto_rewrite", task.rewriteDegree || "light");
      localStorage.setItem("xeaos_goto_source", "dso_distribution");
    } catch { /* 忽略 */ }
    setWorkspace("media_studio");
  };

  const handlePublishToWechat = async () => {
    if (publishingWechat) return;
    setPublishingWechat(true);
    setPublishMessage(null);
    setPublishStatus('submitting');

    // 校验前置条件：必须有内容方向（标题），否则发布无意义
    if (contentDirections.length === 0) {
      setPublishStatus('failed');
      setPublishMessage('❌ 发布失败：今日内容策略为空，请先点击"一键启动"生成策略');
      setPublishingWechat(false);
      return;
    }

    try {
      if (!RuntimeManager.isInitialized()) {
        setPublishStatus('failed');
        setPublishMessage('RuntimeManager 尚未初始化');
        return;
      }

      // v6.3.1 先调用 MediaStudio 生成该方向的真实公众号文章（ContentPackage），再发布
      //（旧实现直接 publish_orchestrate({title,body}) → 不满足 ContentPackage 结构 → 必然失败/空内容）
      const firstDirection = contentDirections[0];
      const topic = firstDirection?.title || 'GEO+DSO 今日策略';
      setPublishMessage(`⏳ 正在生成《${topic}》公众号文章...`);
      const genResp: any = await RuntimeManager.execute(RuntimeName.MediaStudio, 'generate_script', {
        platform: 'wechat',
        topic,
        description: firstDirection?.reason || `围绕「${topic}」写一篇公众号文章，GEO+DSO 策略驱动`,
        context: firstDirection?.targetKeywords?.length
          ? `【目标关键词】${firstDirection.targetKeywords.join('、')}。写作要求：真诚口语化、基于真实经验与踩坑、有可带走的价值点，禁网络腔/夸张/广告腔。`
          : '',
        skipCover: true,
        brand: 'xeaos',
      });
      const genData = (genResp as any)?.data as any;
      const pkg = genData?.data || genData;
      const articleText = pkg?.articleScript?.content
        || pkg?.content
        || (typeof pkg === 'string' ? pkg : '');
      if (!articleText || articleText.length < 100) {
        setPublishStatus('failed');
        setPublishMessage('❌ 公众号文章生成失败（内容为空），请检查 DeepSeek 配置后重试');
        return;
      }

      // 构造符合 ContentRuntime 的发布参数：直接走单平台 publish（公众号）
      const itemPayload = {
        id: `geo-dso-${Date.now()}`,
        title: topic,
        body: articleText,
        contentType: 'markdown' as const,
        tags: firstDirection?.targetKeywords || [],
      };

      let resp: any;
      try {
        resp = await RuntimeManager.execute(
          RuntimeName.Content,
          'publish',
          {
            platform: 'wechat_official',
            item: itemPayload,
            options: { asDraft: true, autoPublish: false },
            source: 'geo_dso_entry',
          }
        );
      } catch (publishErr: any) {
        setPublishStatus('failed');
        setPublishMessage(`❌ 公众号发布失败：${publishErr?.message || String(publishErr)}`);
        return;
      }

      // 关键：验证发布是否真的成功（不再只看 resp.success）
      if (!resp?.success) {
        setPublishStatus('failed');
        const errMsg = resp?.error || '未知错误';
        setPublishMessage(`❌ 发布失败：${errMsg}`);
        return;
      }

      // 检查响应数据是否包含 publishId / mediaId（表示实际提交成功）
      const respData = resp.data as any;
      const innerData = respData?.data || respData; // 双层解包
      const publishId = innerData?.publishId || innerData?.publish_id;
      const mediaId = innerData?.mediaId || innerData?.media_id;

      if (!publishId && !mediaId) {
        // 没有发布 ID 表示未真正提交到微信
        setPublishStatus('failed');
        setPublishMessage(`❌ 发布未生效：响应缺少 publishId/mediaId（响应：${JSON.stringify(innerData).slice(0, 200)}）`);
        return;
      }

      // 进入验证阶段：如有 publishId，轮询发布状态
      if (publishId) {
        setPublishStatus('verifying');
        setPublishMessage(`⏳ 已提交发布（publishId: ${publishId.slice(0, 16)}...），正在验证发布状态...`);

        // 轮询 3 次，每次间隔 2 秒
        let finalStatus: 'success' | 'failed' | 'unknown' = 'unknown';
        for (let attempt = 0; attempt < 3; attempt++) {
          await new Promise((r) => setTimeout(r, 2000));
          try {
            const statusResp = await RuntimeManager.query(RuntimeName.Content, {
              type: 'get_publish_status',
              platform: 'wechat_official',
              publishId,
            });
            const sData = statusResp?.data as any;
            const innerS = sData?.data || sData;
            const state = innerS?.status || innerS?.publish_status || '';
            if (state === 'publish_success' || state === 'success') {
              finalStatus = 'success';
              break;
            }
            if (state === 'publish_failed' || state === 'failed') {
              finalStatus = 'failed';
              break;
            }
            // 其他状态（pending/scheduled）继续轮询
          } catch {
            // 状态查询失败，继续尝试
          }
        }

        if (finalStatus === 'success') {
          setPublishStatus('success');
          setPublishMessage(`✅ 发布成功！微信公众号已收到图文素材（publishId: ${publishId.slice(0, 16)}...）`);
        } else if (finalStatus === 'failed') {
          setPublishStatus('failed');
          setPublishMessage(`❌ 发布失败：微信平台返回 publish_failed（publishId: ${publishId.slice(0, 16)}...）`);
        } else {
          // 状态未知（pending），不算失败但提示用户到后台确认
          setPublishStatus('success');
          setPublishMessage(`⚠️ 已提交发布，状态待微信确认（pending）。请前往微信公众号后台 https://mp.weixin.qq.com 审核发布`);
        }
      } else {
        // 只有 mediaId 没有 publishId（草稿模式）
        setPublishStatus('success');
        setPublishMessage(`✓ 已提交到微信公众号草稿箱（mediaId: ${mediaId.slice(0, 16)}...），请前往公众号后台审核发布`);
      }
    } catch (err: any) {
      setPublishStatus('failed');
      setPublishMessage(`发布异常：${err?.message || String(err)}`);
    } finally {
      setPublishingWechat(false);
    }
  };

  // ============================================
  // 跳转媒体工作室
  // ============================================
  const handleGoToMediaStudio = () => {
    setWorkspace('media_studio');
  };

  // ============================================
  // 渲染
  // ============================================

  return (
    <div className="rounded-lg border border-cyan-500/20 bg-gradient-to-br from-cyan-500/[0.03] to-blue-500/[0.02] p-3">
      {/* 头部 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
            <Globe size={14} className="text-cyan-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white">GEO + DSO 获客策略中控</h3>
            <p className="text-[11px] font-mono text-gray-500 uppercase tracking-wider">
              内容策略 · 分发矩阵 · SEO 机会
              {dataSourceMeta?.dataSource === 'obsidian' && (
                <span className="text-emerald-400 ml-1">· Obsidian 真实数据</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={handlePublishToWechat}
            disabled={publishingWechat}
            className={`flex items-center gap-1 px-2 py-1 text-[12px] font-mono text-white rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
              publishStatus === 'success' ? 'bg-emerald-600 hover:bg-emerald-600' :
              publishStatus === 'failed' ? 'bg-red-600 hover:bg-red-600' :
              'bg-emerald-600/80 hover:bg-emerald-600'
            }`}
            title="将今日策略发布到微信公众号，并验证发布状态"
          >
            {publishingWechat ? (
              <Loader2 size={10} className="animate-spin" />
            ) : publishStatus === 'success' ? (
              <CheckCircle2 size={10} />
            ) : publishStatus === 'failed' ? (
              <XCircle size={10} />
            ) : (
              <Send size={10} />
            )}
            {publishingWechat
              ? (publishStatus === 'verifying' ? '验证中...' : '发布中...')
              : publishStatus === 'success' ? '已发布'
              : publishStatus === 'failed' ? '发布失败，点击重试'
              : '发布到微信公众号'}
          </button>
          <button
            onClick={handleGoToMediaStudio}
            className="flex items-center gap-1 px-2 py-1 text-[12px] font-mono text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded transition-colors"
            title="跳转到媒体工作室生成全套素材"
          >
            <ExternalLink size={10} />
            跳转媒体工作室
          </button>
          <button
            onClick={handleRunGrowthFactory}
            disabled={running}
            className="flex items-center gap-1 px-2 py-1 text-[12px] font-mono text-white bg-cyan-600/80 hover:bg-cyan-600 rounded transition-colors disabled:opacity-40"
            title="一键启动 GEO+DSO+Content 编排"
          >
            {running ? <Loader2 size={10} className="animate-spin" /> : <Play size={10} />}
            {running ? '编排中...' : '一键启动'}
          </button>
          <button
            onClick={loadData}
            disabled={loading}
            className="p-1 text-gray-400 hover:text-gray-200"
            title="刷新数据"
          >
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* 发布状态提示（按状态变色） */}
      {publishMessage && (
        <div className={`mb-2 flex items-center gap-2 px-3 py-1.5 rounded-md border ${
          publishStatus === 'success' ? 'bg-emerald-500/10 border-emerald-500/20' :
          publishStatus === 'failed' ? 'bg-red-500/10 border-red-500/20' :
          'bg-amber-500/10 border-amber-500/20'
        }`}>
          {publishStatus === 'success' ? (
            <CheckCircle2 size={11} className="text-emerald-400 flex-shrink-0" />
          ) : publishStatus === 'failed' ? (
            <XCircle size={11} className="text-red-400 flex-shrink-0" />
          ) : (
            <Send size={11} className="text-amber-400 flex-shrink-0" />
          )}
          <span className={`text-[12px] ${
            publishStatus === 'success' ? 'text-emerald-300' :
            publishStatus === 'failed' ? 'text-red-300' :
            'text-amber-300'
          }`}>{publishMessage}</span>
        </div>
      )}

      {/* toast 提示（执行结果反馈，自动消失） */}
      {toast && (
        <div
          className={`mb-2 flex items-center gap-2 px-3 py-1.5 rounded-md border ${
            toast.type === 'success'
              ? 'bg-emerald-500/10 border-emerald-500/20'
              : 'bg-red-500/10 border-red-500/20'
          }`}
        >
          {toast.type === 'success' ? (
            <CheckCircle2 size={11} className="text-emerald-400 flex-shrink-0" />
          ) : (
            <XCircle size={11} className="text-red-400 flex-shrink-0" />
          )}
          <span
            className={`text-[12px] ${
              toast.type === 'success' ? 'text-emerald-300' : 'text-red-300'
            }`}
          >
            {toast.message}
          </span>
        </div>
      )}

      {/* 错误提示 */}
      {error && (
        <div className="mb-2 flex items-center gap-2 px-3 py-1.5 rounded-md bg-red-500/10 border border-red-500/20">
          <AlertCircle size={11} className="text-red-400 flex-shrink-0" />
          <span className="text-[12px] text-red-300">{error}</span>
        </div>
      )}

      {/* 加载中 */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 size={18} className="animate-spin text-cyan-400" />
          <span className="ml-2 text-[11px] text-gray-400 font-mono">正在从 Obsidian 读取真实数据...</span>
        </div>
      )}

      {/* ============ 4 步收益 SOP 面板（可点击：信源铺设→计划登记→数据复盘→收益转化） ============ */}
      {!loading && (
        <div className="mb-3 rounded-md border border-emerald-500/25 bg-gradient-to-r from-emerald-500/[0.06] via-cyan-500/[0.04] to-blue-500/[0.04] p-2">
          {/* 面板头 */}
          <div className="flex items-center gap-1.5 mb-1.5">
            <ListChecks size={11} className="text-emerald-400" />
            <span className="text-[11px] font-mono text-gray-300 uppercase tracking-wider">
              4 步收益 SOP · 点按任意步骤直达对应模块
            </span>
            <span className="flex-1" />
            <button
              onClick={() => { setActiveSop('source'); setActiveTab('source'); }}
              className="text-[11px] font-mono text-emerald-400 hover:text-emerald-300 border border-emerald-500/30 rounded px-1.5 py-0.5 hover:bg-emerald-500/10 transition"
              title="回到第 1 步：信源铺设指引"
            >
              从第 1 步开始 →
            </button>
          </div>
          {/* 4 步横排（每步可点击，直达对应 Tab） */}
          <div className="grid grid-cols-4 gap-1.5">
            {SOP_STEPS.map((step, i) => {
              const active = activeSop === step.id;
              return (
                <button
                  key={step.id}
                  onClick={() => { setActiveSop(step.id); setActiveTab(step.detailTabs[0] as any); }}
                  className={`group flex items-start gap-1.5 rounded-md border p-1.5 text-left transition-all ${
                    active
                      ? 'border-white/20 bg-white/[0.06]'
                      : 'border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/[0.12]'
                  }`}
                >
                  {/* 步骤序号徽标 */}
                  <div
                    className="w-6 h-6 rounded flex items-center justify-center text-[12px] font-bold flex-shrink-0"
                    style={{ color: step.color, background: `${step.color}1a`, border: `1px solid ${step.color}40` }}
                  >
                    {i + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1">
                      <span className="text-[12px]">{step.icon}</span>
                      <span className="text-[11px] font-semibold text-gray-200 truncate">{step.title}</span>
                    </div>
                    <div className="text-[10px] font-mono text-gray-500 leading-tight mt-0.5">{step.desc}</div>
                    <div className="mt-0.5 text-[10px] font-mono opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: step.color }}>
                      {active ? '● 当前步骤' : '进入 →'}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ============ 三层架构可视化（输入 GEO → 决策 DSO → 输出 GrowthFactory） ============ */}
      {!loading && (
        <div className="mb-3">
          <div className="text-[11px] font-mono text-gray-600 uppercase tracking-wider mb-1">
            三层架构：输入 → 决策 → 输出
          </div>
          <div className="flex items-stretch gap-1">
            {/* ===== 左栏：输入层 GEO（热点采集 / 关键词 / SEO机会 / 内容方向） ===== */}
            <div className="flex-1 rounded-md border border-white/[0.06] bg-white/[0.02] overflow-hidden flex flex-col min-w-0">
              {/* 健康度色条 */}
              <div className="h-0.5 w-full" style={{ background: HEALTH_COLOR[geoHealth] }} />
              <div className="p-1.5 flex flex-col flex-1">
                {/* 标题行 */}
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1">
                    <Database size={10} className="text-cyan-400" />
                    <span className="text-[11px] font-mono text-gray-300">输入层 GEO</span>
                  </div>
                  <span className="text-[11px] font-mono" style={{ color: HEALTH_COLOR[geoHealth] }}>
                    ●{HEALTH_LABEL[geoHealth]}
                  </span>
                </div>
                {/* 可调用标记 */}
                <div className="text-[11px] font-mono mb-1">
                  {RuntimeManager.hasRuntime(RuntimeName.GEO) ? (
                    <span className="text-emerald-400">✓ 可调用</span>
                  ) : (
                    <span className="text-gray-600">✗ 未注册</span>
                  )}
                </div>
                {/* 关键指标摘要 */}
                <div className="space-y-0.5 mb-1.5 flex-1">
                  <div className="flex justify-between text-[11px] font-mono">
                    <span className="text-gray-500">关键词</span>
                    <span className="text-gray-300">{coreKeywords.length > 0 ? coreKeywords.length : '暂无数据'}</span>
                  </div>
                  <div className="flex justify-between text-[11px] font-mono">
                    <span className="text-gray-500">内容方向</span>
                    <span className="text-gray-300">{contentDirections.length > 0 ? contentDirections.length : '暂无数据'}</span>
                  </div>
                  <div className="flex justify-between text-[11px] font-mono">
                    <span className="text-gray-500">SEO机会</span>
                    <span className="text-gray-300">{seoOpportunities.length > 0 ? seoOpportunities.length : '暂无数据'}</span>
                  </div>
                </div>
                {/* 执行按钮：刷新 GEO 策略 */}
                <button
                  onClick={handleExecuteGEO}
                  disabled={geoRunning || !RuntimeManager.hasRuntime(RuntimeName.GEO)}
                  className="flex items-center justify-center gap-1 px-1.5 py-1 text-[11px] font-mono text-white bg-cyan-600/70 hover:bg-cyan-600 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  title="刷新 GEO 策略（热点采集/关键词/SEO机会/内容方向）"
                >
                  {geoRunning ? <Loader2 size={9} className="animate-spin" /> : <Play size={9} />}
                  {geoRunning ? '执行中...' : '执行'}
                </button>
                {/* 执行结果反馈 */}
                {geoLastResult && !geoRunning && (
                  <div className={`text-[11px] font-mono mt-0.5 text-center ${geoLastResult.success ? 'text-emerald-400' : 'text-red-400'}`}>
                    {geoLastResult.success ? '✅ 执行成功' : `❌ 执行失败：${geoLastResult.error || ''}`}
                  </div>
                )}
              </div>
            </div>

            {/* 数据流向箭头：GEO → DSO */}
            <div className="flex items-center justify-center px-0.5">
              <ArrowRight size={12} className="text-gray-600" />
            </div>

            {/* ===== 中栏：决策层 DSO（分发任务 / 矩阵协同 / 品牌一致性） ===== */}
            <div className="flex-1 rounded-md border border-white/[0.06] bg-white/[0.02] overflow-hidden flex flex-col min-w-0">
              <div className="h-0.5 w-full" style={{ background: HEALTH_COLOR[dsoHealth] }} />
              <div className="p-1.5 flex flex-col flex-1">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1">
                    <Layers size={10} className="text-amber-400" />
                    <span className="text-[11px] font-mono text-gray-300">决策层 DSO</span>
                  </div>
                  <span className="text-[11px] font-mono" style={{ color: HEALTH_COLOR[dsoHealth] }}>
                    ●{HEALTH_LABEL[dsoHealth]}
                  </span>
                </div>
                <div className="text-[11px] font-mono mb-1">
                  {RuntimeManager.hasRuntime(RuntimeName.DSO) ? (
                    <span className="text-emerald-400">✓ 可调用</span>
                  ) : (
                    <span className="text-gray-600">✗ 未注册</span>
                  )}
                </div>
                <div className="space-y-0.5 mb-1.5 flex-1">
                  <div className="flex justify-between text-[11px] font-mono">
                    <span className="text-gray-500">分发任务</span>
                    <span className="text-gray-300">{distributionTasks.length > 0 ? distributionTasks.length : '暂无数据'}</span>
                  </div>
                  <div className="flex justify-between text-[11px] font-mono">
                    <span className="text-gray-500">矩阵平台</span>
                    <span className="text-gray-300">{matrixPlatforms.length > 0 ? matrixPlatforms.length : '暂无数据'}</span>
                  </div>
                </div>
                {/* 执行按钮：刷新 DSO 分发 */}
                <button
                  onClick={handleExecuteDSO}
                  disabled={dsoRunning || !RuntimeManager.hasRuntime(RuntimeName.DSO)}
                  className="flex items-center justify-center gap-1 px-1.5 py-1 text-[11px] font-mono text-white bg-amber-600/70 hover:bg-amber-600 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  title="刷新 DSO 分发（任务/矩阵协同/品牌一致性）"
                >
                  {dsoRunning ? <Loader2 size={9} className="animate-spin" /> : <Play size={9} />}
                  {dsoRunning ? '执行中...' : '执行'}
                </button>
                {/* 执行结果反馈 */}
                {dsoLastResult && !dsoRunning && (
                  <div className={`text-[11px] font-mono mt-0.5 text-center ${dsoLastResult.success ? 'text-emerald-400' : 'text-red-400'}`}>
                    {dsoLastResult.success ? '✅ 执行成功' : `❌ 执行失败：${dsoLastResult.error || ''}`}
                  </div>
                )}
              </div>
            </div>

            {/* 数据流向箭头：DSO → GrowthFactory */}
            <div className="flex items-center justify-center px-0.5">
              <ArrowRight size={12} className="text-gray-600" />
            </div>

            {/* ===== 右栏：输出层 GrowthFactory（执行结果 / 内容产出 / 分发执行） ===== */}
            <div className="flex-1 rounded-md border border-white/[0.06] bg-white/[0.02] overflow-hidden flex flex-col min-w-0">
              <div className="h-0.5 w-full" style={{ background: HEALTH_COLOR[gfHealth] }} />
              <div className="p-1.5 flex flex-col flex-1">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1">
                    <Factory size={10} className="text-emerald-400" />
                    <span className="text-[11px] font-mono text-gray-300">输出层 GF</span>
                  </div>
                  <span className="text-[11px] font-mono" style={{ color: HEALTH_COLOR[gfHealth] }}>
                    ●{HEALTH_LABEL[gfHealth]}
                  </span>
                </div>
                <div className="text-[11px] font-mono mb-1">
                  {RuntimeManager.hasRuntime(RuntimeName.GrowthFactory) ? (
                    <span className="text-emerald-400">✓ 可调用</span>
                  ) : (
                    <span className="text-gray-600">✗ 未注册</span>
                  )}
                </div>
                {/* 执行结果摘要 */}
                <div className="space-y-0.5 mb-1.5 flex-1">
                  {gfLastResult ? (
                    <>
                      <div className="flex justify-between text-[11px] font-mono">
                        <span className="text-gray-500">上次执行</span>
                        <span className={gfLastResult.success ? 'text-emerald-400' : 'text-red-400'}>
                          {gfLastResult.success ? '成功' : '失败'}
                        </span>
                      </div>
                      <div className="flex justify-between text-[11px] font-mono">
                        <span className="text-gray-500">时间</span>
                        <span className="text-gray-300">{gfLastResult.time}</span>
                      </div>
                    </>
                  ) : (
                    <div className="text-[11px] font-mono text-gray-600">暂无数据</div>
                  )}
                </div>
                {/* 执行按钮：启动 GrowthFactory 编排 */}
                <button
                  onClick={handleRunGrowthFactory}
                  disabled={running || !RuntimeManager.hasRuntime(RuntimeName.GrowthFactory)}
                  className="flex items-center justify-center gap-1 px-1.5 py-1 text-[11px] font-mono text-white bg-emerald-600/70 hover:bg-emerald-600 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  title="启动 GrowthFactory 编排（GEO→DSO→Content→Publish）"
                >
                  {running ? <Loader2 size={9} className="animate-spin" /> : <Play size={9} />}
                  {running ? '执行中...' : '执行'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ 执行结果展示卡片（9 stage 成功/失败网格） ============ */}
      {lastPipeline && (
        <div className="mb-3 rounded-md border border-white/[0.06] bg-white/[0.02] p-2">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-1.5">
              <Activity size={11} className={lastPipeline.success ? 'text-emerald-400' : 'text-red-400'} />
              <span className="text-[12px] font-mono text-gray-300">执行结果</span>
              <span
                className={`text-[11px] font-mono px-1.5 py-0.5 rounded ${
                  lastPipeline.success
                    ? 'bg-emerald-500/15 text-emerald-400'
                    : 'bg-red-500/15 text-red-400'
                }`}
              >
                {lastPipeline.completedStages}/{lastPipeline.totalStages} stage
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-mono text-gray-500">{lastPipeline.time}</span>
              <button
                onClick={() => setShowPipelineDetail((v) => !v)}
                className="flex items-center gap-0.5 text-[11px] font-mono text-cyan-400 hover:text-cyan-300"
              >
                {showPipelineDetail ? '收起详情' : '查看执行详情'}
                <ChevronDown
                  size={9}
                  className={showPipelineDetail ? 'rotate-180 transition-transform' : 'transition-transform'}
                />
              </button>
            </div>
          </div>
          {/* 9 stage 状态网格 */}
          <div className="grid grid-cols-9 gap-1">
            {lastPipeline.stages.length === 0 ? (
              <div className="col-span-9 text-[11px] font-mono text-gray-600 text-center py-1">
                无 stage 数据
              </div>
            ) : (
              lastPipeline.stages.map((stage, i) => {
                const ok = stage.status === 'completed';
                const failed = stage.status === 'failed';
                return (
                  <div
                    key={i}
                    title={`${stage.name}: ${stage.detail || stage.error || stage.status}`}
                    className={`flex flex-col items-center justify-center py-1 rounded text-[11px] font-mono ${
                      ok
                        ? 'bg-emerald-500/15 text-emerald-400'
                        : failed
                        ? 'bg-red-500/15 text-red-400'
                        : 'bg-gray-500/15 text-gray-400'
                    }`}
                  >
                    <span className="text-[12px] leading-none">
                      {ok ? '✓' : failed ? '✗' : '·'}
                    </span>
                    <span className="mt-0.5 opacity-80">{stage.name}</span>
                  </div>
                );
              })
            )}
          </div>
          {/* 展开详情：每个 stage 的明细 */}
          {showPipelineDetail && (
            <div className="mt-2 pt-2 border-t border-white/[0.06] space-y-0.5">
              {lastPipeline.stages.length === 0 ? (
                <div className="text-[11px] font-mono text-gray-600">执行失败，无 stage 明细</div>
              ) : (
                lastPipeline.stages.map((stage, i) => {
                  const ok = stage.status === 'completed';
                  const failed = stage.status === 'failed';
                  return (
                    <div key={i} className="flex items-start gap-2 text-[11px] font-mono">
                      <span
                        className={
                          ok
                            ? 'text-emerald-400'
                            : failed
                            ? 'text-red-400'
                            : 'text-gray-400'
                        }
                      >
                        {ok ? '✓' : failed ? '✗' : '·'} {stage.name}
                      </span>
                      <span className="text-gray-500 flex-1">
                        {stage.detail || stage.error || stage.status}
                      </span>
                      <span className="text-gray-600">
                        {stage.items > 0 ? `${stage.items} 项` : ''}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}

      {/* ============ 详情区域：5 个 Tab（strategy/seo/gaps/distribution/matrix） ============ */}
      {/* Tab 切换 */}
      {!loading && (
        <div className="flex items-center gap-1 mb-2 flex-wrap">
          {([
            // v6.35 GEO+DSO 分步指导（新用户 onboarding 首位）：现状诊断 + 泛词关联 5 招 + 六步手册
            { id: 'guide' as const, label: '指导', icon: Compass, color: '#06B6D4', count: 0 },
            { id: 'geoinput' as const, label: 'GEO输入', icon: Globe, color: '#06B6D4', count: 0 },
            { id: 'strategy' as const, label: '内容策略', icon: Target, color: '#4D6BFE', count: contentDirections.length },
            { id: 'seo' as const, label: '关键词/SEO', icon: Search, color: '#10B981', count: coreKeywords.length },
            { id: 'gaps' as const, label: '内容差距', icon: AlertCircle, color: '#F97316', count: contentGaps.length },
            { id: 'dso' as const, label: 'DSO决策', icon: Activity, color: '#8B5CF6', count: 0 },
            { id: 'gf' as const, label: 'GF输出', icon: Factory, color: '#EC4899', count: 0 },
            // v6.1.12 用户找不到 AI可见性入口 → 在 AI超市 GEO+DSO 控制台内也挂一个（含一键诊断）
            { id: 'visibility' as const, label: 'AI可见性', icon: Radar, color: '#A855F7', count: 0 },
            { id: 'leads' as const, label: '官网留资', icon: Phone, color: '#34D399', count: 0 },
            { id: 'canvas' as const, label: '策略画布', icon: Grid3x3, color: '#22D3EE', count: 0 },
            { id: 'brand' as const, label: '品牌事实库', icon: BookMarked, color: '#F59E0B', count: 0 },
            { id: 'schema' as const, label: 'Schema部署', icon: Braces, color: '#8B5CF6', count: 0 },
            { id: 'capability' as const, label: '产品能力', icon: Layers, color: '#10B981', count: 0 },
            { id: 'source' as const, label: '信源铺设', icon: Share2, color: '#3B82F6', count: 0 },
            { id: 'command' as const, label: '管控中台', icon: Database, color: '#F59E0B', count: 0 },
            { id: 'kg' as const, label: '知识图谱', icon: Network, color: '#22D3EE', count: 0 },
            { id: 'compete' as const, label: '竞品对标', icon: Swords, color: '#F59E0B', count: 0 },
            { id: 'ab' as const, label: 'A/B实验', icon: FlaskConical, color: '#A855F7', count: 0 },
            { id: 'routing' as const, label: '线索打分', icon: Route, color: '#10B981', count: 0 },
          ]).map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded text-[12px] font-mono transition-all ${
                  active ? 'text-white bg-white/[0.04]' : 'text-gray-500 hover:text-gray-300'
                }`}
                style={active ? { color: tab.color } : {}}
              >
                <Icon size={10} />
                {tab.label}
                <span className="ml-0.5 text-[11px] opacity-60">({tab.count})</span>
              </button>
            );
          })}
        </div>
      )}

      {/* 内容区 */}
      {!loading && (
        <>
        {/* v6.1.12 AI可见性 Tab：独立全高渲染（含一键诊断），不套 360px 限制 */}
        {/* v6.35 GEO+DSO 分步指导 Tab：onboarding 向导（诊断+六步手册+黄金5招） */}
        {activeTab === 'guide' ? (
          <div className="h-[560px] overflow-y-auto pr-0.5">
            <GeoDsoGuidePanel onJumpTab={(tab) => setActiveTab(tab as any)} />
          </div>
        ) : activeTab === 'geoinput' ? (
          <div className="h-[520px] overflow-y-auto">
            <GeoInputEnhanced />
          </div>
        ) : activeTab === 'dso' ? (
          <div className="h-[520px] overflow-y-auto">
            <DsoDecisionEngine />
          </div>
        ) : activeTab === 'gf' ? (
          <div className="h-[520px] overflow-y-auto">
            <GfOutputClosure />
          </div>
        ) : activeTab === 'command' ? (
          <div className="h-[520px] overflow-y-auto">
            <GeoDsoCommandCenter />
          </div>
        ) : activeTab === 'kg' ? (
          <div className="h-[520px] overflow-y-auto">
            <KnowledgeGraphPanel />
          </div>
        ) : activeTab === 'compete' ? (
          <div className="h-[520px] overflow-y-auto">
            <CompetitorVisibilityPanel />
          </div>
        ) : activeTab === 'ab' ? (
          <div className="h-[520px] overflow-y-auto">
            <ContentABExperimentPanel />
          </div>
        ) : activeTab === 'routing' ? (
          <div className="h-[520px] overflow-y-auto">
            <LeadRoutingPanel />
          </div>
        ) : activeTab === 'visibility' ? (
          <div className="h-[520px]">
            <AiVisibilityMonitor />
          </div>
        ) : activeTab === 'leads' ? (
          <div className="h-[460px] overflow-y-auto">
            <LeadCapturePanel />
          </div>
        ) : activeTab === 'canvas' ? (
          <div className="h-[460px] overflow-y-auto">
            <GeoStrategyCanvas directions={contentDirections} keywords={coreKeywords} />
          </div>
        ) : activeTab === 'brand' ? (
          <div className="h-[480px] overflow-y-auto">
            <BrandLibraryPanel />
          </div>
        ) : activeTab === 'schema' ? (
          <div className="h-[480px] overflow-y-auto">
            <SchemaDeployerPanel />
          </div>
        ) : activeTab === 'capability' ? (
          <div className="h-[480px] overflow-y-auto">
            <ProductCapabilityPanel />
          </div>
        ) : activeTab === 'source' ? (
          <div className="h-[480px] overflow-y-auto">
            <SourceDeploymentPanel />
          </div>
        ) : (
        <div className="max-h-[360px] overflow-y-auto">
          {/* ============ 内容策略 Tab ============ */}
          {activeTab === 'strategy' && (
            <div className="space-y-2">
              {/* v5.2.3 系统关联总览：GEO 策略 ↔ 资产库/CRM 获客闭环 */}
              <div className="flex items-center gap-2 rounded border border-cyan-500/15 bg-cyan-500/[0.03] px-2 py-1.5 text-[11px] font-mono">
                <Database size={10} className="text-cyan-400 flex-shrink-0" />
                <span className="text-gray-400">系统关联：</span>
                <span className="text-cyan-300">资产库 {assetTotal} 个产物</span>
                <span className="text-gray-600">|</span>
                <span className={leadTotal !== null ? "text-emerald-300" : "text-gray-500"}>
                  CRM {leadTotal ?? 0} 条线索
                </span>
                <span className="flex-1" />
                <button
                  onClick={() => setWorkspace("media_studio")}
                  className="text-cyan-400 hover:text-cyan-200 border border-cyan-500/30 rounded px-1.5 py-0.5 hover:bg-cyan-500/10 transition"
                >
                  查看资产库 →
                </button>
                <button
                  onClick={() => setWorkspace("crm")}
                  className="text-emerald-400 hover:text-emerald-200 border border-emerald-500/30 rounded px-1.5 py-0.5 hover:bg-emerald-500/10 transition"
                >
                  线索跟进 →
                </button>
              </div>
              {contentDirections.length === 0 ? (
                <p className="text-[11px] text-gray-600 text-center py-4">
                  暂无内容方向，请确保 Obsidian 中有今日新闻或运营计划
                </p>
              ) : (
                contentDirections.map((dir, i) => {
                  const platform = PLATFORM_DISPLAY[dir.platform] || { name: dir.platform, color: '#6B7280', icon: '📌' };
                  const contentTypeLabel: Record<string, string> = {
                    tutorial: '教程', analysis: '分析', case_study: '案例', opinion: '观点', news: '资讯',
                  };
                  return (
                    <div key={i} className="p-2 rounded border border-white/[0.04] bg-white/[0.01]">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="text-[12px]">{platform.icon}</span>
                          <span className="text-[11px] text-gray-200 font-medium truncate">{dir.title}</span>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className="text-[11px] font-mono px-1 py-0.5 rounded bg-blue-500/10 text-blue-300">
                            {contentTypeLabel[dir.contentType] || dir.contentType}
                          </span>
                          <span
                            className="text-[11px] font-mono px-1.5 py-0.5 rounded"
                            style={{ background: `${platform.color}20`, color: platform.color }}
                          >
                            {platform.name}
                          </span>
                          <button
                            onClick={() => handleGoGenerate(dir)}
                            title="跳到内容工厂，用这个话题直接生成文章"
                            className="flex items-center gap-0.5 text-[11px] font-mono px-1.5 py-0.5 rounded bg-purple-600/20 hover:bg-purple-600/40 text-purple-300 border border-purple-500/30 transition"
                          >
                            <Send size={7} /> 生成 →
                          </button>
                          {/* v5.2.3 GEO ↔ 资产库关联：该方向已有产物时显示，可一键跳到内容工厂资产库查看 */}
                          {(() => {
                            const hit = [...assetIndex.entries()].find(([t]) =>
                              dir.title && (t === dir.title || t.includes(dir.title) || dir.title.includes(t))
                            );
                            if (!hit) return null;
                            return (
                              <button
                                onClick={() => {
                                  // 跳内容工厂资产库（把关联话题写入 localStorage 供资产库筛选）
                                  try { localStorage.setItem("xeaos_goto_topic", dir.title); } catch { /* 忽略 */ }
                                  setWorkspace("media_studio");
                                }}
                                title={`资产库已有产物「${hit[1].platform} · ${hit[0]}」，点击到内容工厂查看`}
                                className="flex items-center gap-0.5 text-[11px] font-mono px-1.5 py-0.5 rounded bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-300 border border-emerald-500/30 transition"
                              >
                                <Database size={7} /> 已有产物 · 查看
                              </button>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="text-[11px] text-gray-500 mb-1">{dir.reason}</div>
                      {/* 专业指标：ROI、流量、转化、CTR、字数 */}
                      <div className="grid grid-cols-5 gap-1 mb-1 text-[11px] font-mono">
                        <div className="text-center px-1 py-0.5 rounded bg-white/[0.03]">
                          <div className="text-gray-600">ROI</div>
                          <div className={`font-bold ${dir.roiScore >= 60 ? 'text-emerald-400' : dir.roiScore >= 30 ? 'text-yellow-400' : 'text-red-400'}`}>
                            {dir.roiScore}
                          </div>
                        </div>
                        <div className="text-center px-1 py-0.5 rounded bg-white/[0.03]">
                          <div className="text-gray-600">月流量</div>
                          <div className="text-blue-400">{dir.estimatedTraffic}</div>
                        </div>
                        <div className="text-center px-1 py-0.5 rounded bg-white/[0.03]">
                          <div className="text-gray-600">转化率</div>
                          <div className="text-cyan-400">{(dir.conversionPotential * 100).toFixed(1)}%</div>
                        </div>
                        <div className="text-center px-1 py-0.5 rounded bg-white/[0.03]">
                          <div className="text-gray-600">CTR</div>
                          <div className="text-emerald-400">{(dir.estimatedCTR * 100).toFixed(1)}%</div>
                        </div>
                        <div className="text-center px-1 py-0.5 rounded bg-white/[0.03]">
                          <div className="text-gray-600">字数</div>
                          <div className="text-gray-300">{dir.recommendedWordCount}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-wrap text-[11px] font-mono">
                        {dir.targetKeywords.slice(0, 4).map((kw) => (
                          <span key={kw} className="px-1 py-0.5 rounded bg-white/[0.04] text-gray-400">
                            {kw}
                          </span>
                        ))}
                      </div>
                      {/* ===== GEO 专业增强（v5.2）：目标模型 / 知识块 / E-E-A-T ===== */}
                      {dir.primaryModel && (
                        <div className="flex items-center gap-1 mt-1.5">
                          <Sparkles size={8} className="text-purple-400" />
                          <span className="text-[11px] font-mono text-purple-300">目标模型：{dir.primaryModel}</span>
                          <span className="text-[11px] font-mono text-gray-600">
                            （优先投放到该模型偏好的内容生态）
                          </span>
                        </div>
                      )}
                      {dir.knowledgeBlocks && dir.knowledgeBlocks.length > 0 && (
                        <div className="mt-1.5 rounded border border-purple-500/15 bg-purple-500/[0.03] p-1.5">
                          <div className="flex items-center gap-1 mb-1">
                            <BookOpen size={8} className="text-purple-400" />
                            <span className="text-[11px] font-mono text-purple-300 uppercase tracking-wider">
                              独立成立的知识块（可被模型摘取复述）
                            </span>
                          </div>
                          <div className="space-y-1">
                            {dir.knowledgeBlocks.slice(0, 3).map((kb, j) => (
                              <div key={j} className="text-[11px] font-mono leading-relaxed">
                                <span className="text-cyan-300">Q: {kb.question}</span>
                                <span className="text-gray-500"> → </span>
                                <span className="text-gray-400">{kb.answer}</span>
                                {kb.source && <span className="text-gray-600">（{kb.source}）</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {dir.eeaT && (
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          <span className="flex items-center gap-0.5 text-[11px] font-mono text-emerald-400">
                            <ShieldCheck size={8} /> E-E-A-T: {dir.eeaT.authorIdentity}
                          </span>
                          <span className="text-[11px] font-mono text-gray-600">
                            信源：{dir.eeaT.citationSources.join("、")} · 核验：
                            <span style={{ color: dir.eeaT.factCheckStatus === 'verified' ? '#10B981' : dir.eeaT.factCheckStatus === 'pending' ? '#F59E0B' : '#6B7280' }}>
                              {dir.eeaT.factCheckStatus === 'verified' ? '已核验' : dir.eeaT.factCheckStatus === 'pending' ? '待核验' : '不适用'}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              {/* 品牌关键词 */}
              {brandKeywords.length > 0 && (
                <div className="mt-2 pt-2 border-t border-white/[0.04]">
                  <div className="text-[11px] font-mono text-gray-500 mb-1">品牌关键词（来自品牌圣经）</div>
                  <div className="flex flex-wrap gap-1">
                    {brandKeywords.map((kw) => (
                      <span key={kw} className="px-1.5 py-0.5 rounded text-[11px] font-mono bg-purple-500/10 text-purple-300 border border-purple-500/20">
                        {kw}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ============ 关键词/SEO Tab ============ */}
          {activeTab === 'seo' && (
            <div className="space-y-3">
              {/* 核心关键词 */}
              <div>
                <div className="text-[11px] font-mono text-gray-500 mb-1.5 uppercase tracking-wider">
                  核心关键词（来自今日新闻+产品矩阵）— 含 CPC/意图/SERP 特征/趋势
                </div>
                <div className="space-y-1">
                  {coreKeywords.slice(0, 8).map((kw, i) => {
                    const intentLabel: Record<string, string> = {
                      informational: '信息', commercial: '商业', transactional: '交易', navigational: '导航',
                    };
                    const intentColor: Record<string, string> = {
                      informational: '#60A5FA', commercial: '#F59E0B', transactional: '#10B981', navigational: '#A78BFA',
                    };
                    const trendLabel: Record<string, string> = { rising: '↑上升', stable: '→平稳', declining: '↓下降' };
                    const trendColor: Record<string, string> = { rising: '#10B981', stable: '#9CA3AF', declining: '#EF4444' };
                    return (
                      <div key={i} className="py-1.5 px-2 rounded border border-white/[0.04] bg-white/[0.01]">
                        <div className="flex items-center justify-between mb-0.5">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[11px] text-gray-200">{kw.keyword}</span>
                            <span
                              className="text-[11px] font-mono px-1 py-0.5 rounded"
                              style={{ background: `${DIFFICULTY_COLOR[kw.difficulty]}20`, color: DIFFICULTY_COLOR[kw.difficulty] }}
                            >
                              {kw.difficulty}
                            </span>
                            <span
                              className="text-[11px] font-mono px-1 py-0.5 rounded"
                              style={{ background: `${intentColor[kw.intent]}20`, color: intentColor[kw.intent] }}
                            >
                              {intentLabel[kw.intent]}
                            </span>
                            <span className="text-[11px] font-mono" style={{ color: trendColor[kw.trend] }}>
                              {trendLabel[kw.trend]}
                            </span>
                          </div>
                          <span className="text-[11px] font-mono text-emerald-400">¥{kw.cpc}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[11px] font-mono">
                          <span className="text-blue-400">搜索量 {kw.searchVolume.toLocaleString()}</span>
                          <span className="text-gray-500">竞争 {(kw.competition * 100).toFixed(0)}%</span>
                          <span className="text-gray-400">CPC ¥{kw.cpc}</span>
                        </div>
                        <div className="flex items-center gap-1 flex-wrap mt-0.5">
                          {kw.serpFeatures.map((sf) => (
                            <span key={sf} className="text-[11px] font-mono px-1 py-0.5 rounded bg-cyan-500/10 text-cyan-300">
                              {sf}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* SEO 机会 */}
              <div>
                <div className="text-[11px] font-mono text-gray-500 mb-1.5 uppercase tracking-wider">
                  SEO 机会（来自营销知识库）— 含 SERP 特征/内容差距/竞争强度
                </div>
                <div className="space-y-1">
                  {seoOpportunities.slice(0, 8).map((opp, i) => {
                    const platform = PLATFORM_DISPLAY[opp.platform] || { name: opp.platform, color: '#6B7280', icon: '🔍' };
                    return (
                      <div key={i} className="py-1 px-2 rounded border border-white/[0.04] bg-white/[0.01]">
                        <div className="flex items-center justify-between mb-0.5">
                          <div className="flex items-center gap-1 min-w-0">
                            <span className="text-[12px]">{platform.icon}</span>
                            <span className="text-[12px] text-gray-300 truncate">{opp.keyword}</span>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span className="text-[11px] font-mono px-1 py-0.5 rounded bg-cyan-500/10 text-cyan-300">
                              {opp.serpFeature}
                            </span>
                            <span className="text-[12px] font-mono font-bold" style={{ color: opp.opportunity > 80 ? '#10B981' : '#F59E0B' }}>
                              {opp.opportunity}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-[11px] font-mono">
                          <span className="text-orange-300">差距：{opp.contentGap}</span>
                          <span className="text-gray-500">竞争强度 {(opp.competitorStrength * 100).toFixed(0)}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ============ 内容差距 Tab ============ */}
          {activeTab === 'gaps' && (
            <div className="space-y-2">
              {contentGaps.length === 0 ? (
                <p className="text-[11px] text-gray-600 text-center py-4">暂无内容差距分析数据</p>
              ) : (
                contentGaps.map((gap, i) => (
                  <div key={i} className="p-2 rounded border border-orange-500/15 bg-orange-500/[0.02]">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <span className="text-[11px] text-gray-200 font-medium">{gap.topic}</span>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <span className="text-[11px] font-mono px-1 py-0.5 rounded bg-red-500/10 text-red-300">
                          竞品：{gap.competitor}
                        </span>
                        <span
                          className="text-[11px] font-mono px-1 py-0.5 rounded"
                          style={{ background: `${DIFFICULTY_COLOR[gap.difficulty]}20`, color: DIFFICULTY_COLOR[gap.difficulty] }}
                        >
                          {gap.difficulty}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-[11px] font-mono mb-1">
                      <span className="text-blue-400">预估流量 {gap.estimatedTraffic}/月</span>
                    </div>
                    <div className="text-[11px] text-orange-200">{gap.action}</div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ============ 分发计划 Tab ============ */}
          {activeTab === 'distribution' && (
            <div className="space-y-2">
              {distributionTasks.length === 0 ? (
                <p className="text-[11px] text-gray-600 text-center py-4">暂无分发任务</p>
              ) : (
                distributionTasks.map((task, i) => {
                  const targetPlatform = PLATFORM_DISPLAY[task.targetPlatform] || { name: task.targetPlatform, color: '#6B7280', icon: '📌' };
                  return (
                    <div key={i} className="p-2 rounded border border-white/[0.04] bg-white/[0.01]">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <span className="text-[11px] text-gray-200 font-medium truncate flex-1">{task.contentTitle}</span>
                        <span
                          className={`text-[11px] font-mono px-1 py-0.5 rounded flex-shrink-0 ${
                            task.priority === 'high' ? 'bg-red-500/10 text-red-400' : 'bg-yellow-500/10 text-yellow-400'
                          }`}
                        >
                          {task.priority}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-[11px] font-mono text-gray-500 mb-1">
                        <span className="text-[12px]">{targetPlatform.icon}</span>
                        <span style={{ color: targetPlatform.color }}>{targetPlatform.name}</span>
                        <span>· 最佳时间 {task.bestTime}</span>
                        <span>· 预估触达 {task.estimatedReach.toLocaleString()}</span>
                      </div>
                      <div className="text-[11px] text-gray-400">{task.repurposeSuggestion}</div>
                      {/* ===== GEO 专业增强（v5.2）：首发标注 / 目标模型 / 差异化改写 ===== */}
                      <div className="flex items-center gap-1.5 flex-wrap mt-1">
                        {task.isFirstPublish ? (
                          <span className="flex items-center gap-0.5 text-[11px] font-mono px-1 py-0.5 rounded bg-emerald-500/10 text-emerald-400">
                            <BadgeCheck size={8} /> 官方首发
                          </span>
                        ) : (
                          <span className="flex items-center gap-0.5 text-[11px] font-mono px-1 py-0.5 rounded bg-gray-500/10 text-gray-500">
                            <Copy size={8} /> 差异化改写分发
                          </span>
                        )}
                        {task.targetModel && (
                          <span className="flex items-center gap-0.5 text-[11px] font-mono px-1 py-0.5 rounded bg-purple-500/10 text-purple-300">
                            <Sparkles size={8} /> 目标模型：{task.targetModel}
                          </span>
                        )}
                        {task.rewriteDegree && task.rewriteDegree !== 'none' && (
                          <span
                            className={`flex items-center gap-0.5 text-[11px] font-mono px-1 py-0.5 rounded ${
                              task.rewriteDegree === 'deep'
                                ? 'bg-orange-500/10 text-orange-300'
                                : task.rewriteDegree === 'medium'
                                ? 'bg-yellow-500/10 text-yellow-300'
                                : 'bg-blue-500/10 text-blue-300'
                            }`}
                          >
                            <AlertCircle size={8} />
                            改写度：{task.rewriteDegree === 'deep' ? '深度' : task.rewriteDegree === 'medium' ? '中度' : '轻度'}
                          </span>
                        )}
                      </div>
                      {task.modelEcosystem && (
                        <div className="text-[11px] font-mono text-gray-600 mt-0.5">{task.modelEcosystem}</div>
                      )}
                      {task.duplicateRisk && task.duplicateRisk.length > 0 && (
                        <div className="text-[11px] font-mono text-amber-500/70 mt-0.5">⚠ {task.duplicateRisk}</div>
                      )}
                      {/* v6.3.1 一键生成该平台脚本：携带 平台+标题+改写度 跳转内容工厂 */}
                      <button
                        onClick={() => handleGoGenerateTask(task)}
                        className="mt-1.5 flex items-center gap-1 px-2 py-1 rounded text-[11px] font-mono text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/25 transition-colors"
                        title={`跳转内容工厂，按${targetPlatform.name}规则生成脚本`}
                      >
                        <Send size={9} /> 生成 {targetPlatform.name} 脚本
                      </button>
                    </div>
                  );
                })
              )}
              {/* 矩阵协同 */}
              {matrixSynergy.length > 0 && (
                <div className="mt-2 pt-2 border-t border-white/[0.04]">
                  <div className="text-[11px] font-mono text-gray-500 mb-1.5 uppercase tracking-wider">矩阵协同</div>
                  {matrixSynergy.map((syn, i) => (
                    <div key={i} className="text-[12px] text-gray-400 mb-1">
                      <span className="text-cyan-400">{syn.platforms.join(' + ')}</span>
                      <span className="ml-1">{syn.action}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* 品牌 Checklist */}
              {brandChecklist.length > 0 && (
                <div className="mt-2 pt-2 border-t border-white/[0.04]">
                  <div className="text-[11px] font-mono text-gray-500 mb-1.5 uppercase tracking-wider">品牌一致性检查</div>
                  {brandChecklist.map((item, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-[12px] text-gray-400 mb-0.5">
                      <CheckCircle2 size={9} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ============ 矩阵状态 Tab ============ */}
          {activeTab === 'matrix' && (
            <div className="space-y-1.5">
              {matrixPlatforms.length === 0 ? (
                <p className="text-[11px] text-gray-600 text-center py-4">暂无矩阵状态数据</p>
              ) : (
                matrixPlatforms.map((p, i) => {
                  const config = PLATFORM_DISPLAY[p.platform] || { name: p.platform, color: '#6B7280', icon: '📌' };
                  return (
                    <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded border border-white/[0.04] bg-white/[0.01]">
                      <div className="flex items-center gap-2">
                        <span className="text-[14px]">{config.icon}</span>
                        <div>
                          <div className="text-[11px] text-gray-200">{config.name}</div>
                          <div className="text-[11px] font-mono text-gray-500">{p.accountName}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-center">
                          <div className="text-[11px] text-gray-500">今日发布</div>
                          <div className={`text-[11px] font-mono font-bold ${p.todayPublished > 0 ? 'text-emerald-400' : 'text-gray-600'}`}>
                            {p.todayPublished}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-[11px] text-gray-500">健康度</div>
                          <div className={`text-[11px] font-mono font-bold ${p.health >= 80 ? 'text-emerald-400' : p.health >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                            {p.health}
                          </div>
                        </div>
                        <span className={`flex items-center gap-1 text-[11px] font-mono ${
                          p.status === 'active' ? 'text-emerald-400' : 'text-red-400'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            p.status === 'active' ? 'bg-emerald-400' : 'bg-red-400'
                          }`} />
                          {p.status === 'active' ? '已配置' : '未配置'}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
        )}
        </>
      )}

      {/* 底部说明 */}
      <div className="mt-2 pt-2 border-t border-white/[0.04] flex items-center justify-between text-[11px] font-mono text-gray-600">
        <span className="flex items-center gap-1">
          <Brain size={9} className="text-cyan-600" />
          数据来源：Obsidian Vault（营销知识库+品牌圣经+发布记录）
        </span>
        {dataSourceMeta && (
          <span>
            {dataSourceMeta.newsArticlesFound || 0} 新闻 · {dataSourceMeta.marketingDocsRead || 0} 营销文档
          </span>
        )}
      </div>
    </div>
  );
}
